/**
 * Central Consumer Hook (Web)
 *
 * ALL authorization decisions in the web UI flow through this hook.
 * Central is the SOLE AUTHORITY for access, capabilities, and permissions.
 *
 * This is a thin wrapper: reads app-local Redux state, builds a CentralContext,
 * and delegates pure authorization checks to @levelup/central-context.
 *
 * Rules:
 * - NO local wildcard interpretation
 * - Capability-first check, exact scope fallback only
 * - Fail-closed: boot incomplete or missing data = deny
 * - APP_ID scoped
 */

import { useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { APP_ID } from '../config/centralAuth';
import {
  selectBootCompleted,
  selectSuiteAppsNormalized,
  selectSwitchContextMeta,
} from '../store/slices/centralAuthSlice';
import { selectBootCurrentWorkspace } from '../store/slices/bootContextSlice';

// Pure logic from shared package
import {
  hasAppAccess as sharedHasAppAccess,
  hasCapability as sharedHasCapability,
  hasWorkspacePermission as sharedHasWorkspacePermission,
  hasRole as sharedHasRole,
  hasScope as sharedHasScope,
} from '@levelup/central-context';
import type { CentralContext, WorkspacePermissionKey, CentralRole } from '@levelup/central-context';

// Re-export shared types for consumers
export type { WorkspacePermissionKey as WorkspacePermission, CentralRole, CentralContext };

/**
 * Central consumer hook — provides fail-closed authorization checks.
 *
 * All checks return false if boot is incomplete (fail-closed).
 * Pure authorization logic is delegated to @levelup/central-context.
 */
export function useCentralConsumer() {
  const bootCompleted = useSelector(selectBootCompleted);
  const workspace = useSelector(selectBootCurrentWorkspace);
  const apps = useSelector(selectSuiteAppsNormalized);
  const meta = useSelector(selectSwitchContextMeta);

  // Build the CentralContext that shared pure functions expect.
  // The boot context workspace has role, scopes, capabilities, permissions.
  const context: CentralContext | null = useMemo(() => {
    if (!bootCompleted || !workspace) return null;
    return {
      currentWorkspace: {
        role: workspace.role,
        scopes: workspace.scopes,
        capabilities: workspace.capabilities,
        permissions: workspace.permissions,
      },
      apps: apps.map((a) => ({ appId: a.id })),
      meta: meta ?? undefined,
    };
  }, [bootCompleted, workspace, apps, meta]);

  /**
   * Check if this app (APP_ID) is in the Central apps[] array.
   */
  const hasAppAccess = useCallback((): boolean => {
    if (!context) return false;
    return sharedHasAppAccess(context, APP_ID);
  }, [context]);

  /**
   * Check if the current workspace grants a capability.
   *
   * Capability-first if meta.features.capabilities is enabled,
   * otherwise exact scope fallback (NO wildcards).
   */
  const hasCapability = useCallback(
    (domain: string, action: string): boolean => {
      if (!context) return false;
      return sharedHasCapability(context, APP_ID, domain, action);
    },
    [context]
  );

  /**
   * Check if the user has a workspace-level permission.
   */
  const hasWorkspacePermission = useCallback(
    (permission: WorkspacePermissionKey): boolean => {
      if (!context) return false;
      return sharedHasWorkspacePermission(context, permission);
    },
    [context]
  );

  /**
   * Check if the user's workspace role is one of the allowed roles.
   * Owner does NOT automatically bypass.
   */
  const hasWorkspaceRole = useCallback(
    (...allowedRoles: CentralRole[]): boolean => {
      if (!context) return false;
      return sharedHasRole(context, allowedRoles);
    },
    [context]
  );

  /**
   * Check a specific entitlement key (exact match, fail-closed).
   */
  const hasEntitlement = useCallback(
    (key: string): boolean => {
      if (!context) return false;
      return sharedHasScope(context, key);
    },
    [context]
  );

  return {
    bootCompleted,
    workspace,
    apps,
    hasAppAccess,
    hasCapability,
    hasWorkspacePermission,
    hasWorkspaceRole,
    hasEntitlement,
  };
}
